import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import CronBuilder from "./CronBuilder";

interface SubscribeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  queryId: string;
  queryName: string;
}

const SubscribeDialog = ({ open, onOpenChange, queryId, queryName }: SubscribeDialogProps) => {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const userEmail = user?.email || "";

  const [schedule, setSchedule] = useState("0 9 * * *");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [includeUserEmail, setIncludeUserEmail] = useState(true);
  const [enabled, setEnabled] = useState(true);

  // Fetch existing subscription
  const { data: existing, isLoading } = useQuery({
    queryKey: ["query-subscription", queryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("query_subscriptions" as any)
        .select("*")
        .eq("saved_query_id", queryId)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
    enabled: open,
  });

  // Populate form from existing subscription
  useEffect(() => {
    if (existing) {
      setSchedule(existing.schedule);
      const recs = (existing.recipients as string[]) || [];
      setIncludeUserEmail(recs.includes(userEmail));
      setRecipients(recs.filter((r: string) => r !== userEmail));
      setEnabled(existing.enabled);
    } else {
      setSchedule("0 9 * * *");
      setRecipients([]);
      setIncludeUserEmail(true);
      setEnabled(true);
    }
  }, [existing, userEmail]);

  const allRecipients = () => {
    const list = [...recipients];
    if (includeUserEmail && userEmail) list.unshift(userEmail);
    return list;
  };

  const upsertMutation = useMutation({
    mutationFn: async () => {
      const finalRecipients = allRecipients();
      if (finalRecipients.length === 0) throw new Error("At least one recipient required");

      if (existing) {
        const { error } = await supabase
          .from("query_subscriptions" as any)
          .update({ schedule, recipients: finalRecipients, enabled } as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("query_subscriptions" as any)
          .insert({
            saved_query_id: queryId,
            schedule,
            recipients: finalRecipients,
            enabled,
          } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["query-subscription", queryId] });
      queryClient.invalidateQueries({ queryKey: ["query-subscriptions-all"] });
      toast.success(existing ? "Subscription updated" : "Subscription created");
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message || "Failed to save subscription"),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("query_subscriptions" as any)
        .delete()
        .eq("id", existing.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["query-subscription", queryId] });
      queryClient.invalidateQueries({ queryKey: ["query-subscriptions-all"] });
      toast.success("Subscription removed");
      onOpenChange(false);
    },
    onError: () => toast.error("Failed to remove subscription"),
  });

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    if (recipients.includes(email) || (email === userEmail && includeUserEmail)) {
      toast.error("Email already added");
      return;
    }
    setRecipients((prev) => [...prev, email]);
    setNewEmail("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Subscribe to Query
          </DialogTitle>
          <DialogDescription>
            Schedule "{queryName}" to be sent as a CSV attachment via email.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4">Loading…</p>
        ) : (
          <div className="space-y-5">
            {/* Schedule */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Schedule</Label>
              <CronBuilder value={schedule} onChange={setSchedule} />
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Recipients</Label>

              <div className="flex items-center gap-3">
                <Switch
                  checked={includeUserEmail}
                  onCheckedChange={setIncludeUserEmail}
                  id="include-self"
                />
                <Label htmlFor="include-self" className="text-sm cursor-pointer">
                  Send to my email ({userEmail})
                </Label>
              </div>

              {recipients.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {recipients.map((email) => (
                    <Badge key={email} variant="secondary" className="gap-1 pr-1">
                      {email}
                      <button
                        onClick={() => setRecipients((prev) => prev.filter((e) => e !== email))}
                        className="ml-1 rounded-full p-0.5 hover:bg-muted"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  placeholder="Add recipient email..."
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                  className="flex-1"
                />
                <Button variant="outline" size="sm" onClick={addEmail} type="button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Enabled */}
            {existing && (
              <div className="flex items-center gap-3">
                <Switch checked={enabled} onCheckedChange={setEnabled} id="sub-enabled" />
                <Label htmlFor="sub-enabled" className="text-sm cursor-pointer">
                  Subscription enabled
                </Label>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex gap-2 sm:justify-between">
          {existing && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              Unsubscribe
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => upsertMutation.mutate()}
              disabled={upsertMutation.isPending || isLoading}
            >
              {existing ? "Update" : "Subscribe"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeDialog;
